import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNamedPresets } from '@/hooks/useNamedPresets'
import { useChartLayoutPreferences } from '@/hooks/useChartLayoutPreferences'
import {
  normalizeScreenerChartPreset, readChartPanelWidths,
  type ChartPanelWidths, type ChartViewMode, type ScreenerChartPreset,
} from '@/lib/screenerChartPreferences'
import type { User } from '@/types'

interface Options {
  user: User | null
  orderStorageKey: string
  panelWidthsStorageKey: string
  mode: ChartViewMode
  onModeChange: (mode: ChartViewMode) => void
  priceExchange: string
  onPriceExchangeChange: (exchange: string) => void
  leftOpen: boolean
  onLeftOpenChange: (open: boolean) => void
  rightOpen: boolean
  onRightOpenChange: (open: boolean) => void
  sidebarTab: 'discovery' | 'history'
  onSidebarTabChange: (tab: 'discovery' | 'history') => void
  disabled?: boolean
}

export function useScreenerChartPresets(options: Options) {
  const { orderStorageKey, panelWidthsStorageKey } = options
  const layout = useChartLayoutPreferences(orderStorageKey)
  const savedWidths = useMemo(() => readChartPanelWidths(panelWidthsStorageKey), [panelWidthsStorageKey])
  const [widthState, setWidthState] = useState({ storageKey: panelWidthsStorageKey, value: savedWidths })
  const panelWidths = widthState.storageKey === panelWidthsStorageKey ? widthState.value : savedWidths
  const onPanelWidthsChange = useCallback((value: ChartPanelWidths) => {
    setWidthState({ storageKey: panelWidthsStorageKey, value })
  }, [panelWidthsStorageKey])

  useEffect(() => {
    if (widthState.storageKey !== panelWidthsStorageKey) return
    try { localStorage.setItem(panelWidthsStorageKey, JSON.stringify(widthState.value)) } catch { /* Optional storage. */ }
  }, [widthState, panelWidthsStorageKey])

  const value: ScreenerChartPreset = {
    version: 1,
    mode: options.mode,
    priceExchange: options.priceExchange,
    ...layout.controls.value,
    workspace: { widths: panelWidths, leftOpen: options.leftOpen, rightOpen: options.rightOpen, sidebarTab: options.sidebarTab },
  }
  const presets = useNamedPresets<ScreenerChartPreset>({
    user: options.user,
    preferenceKey: 'screener_chart_presets',
    value,
    disabled: options.disabled,
    normalize: normalizeScreenerChartPreset,
    onApply: (preset) => {
      // Apply the complete snapshot so omitted heights return to their defaults.
      layout.apply(preset)
      onPanelWidthsChange(preset.workspace.widths)
      options.onModeChange(preset.mode)
      options.onPriceExchangeChange(preset.priceExchange)
      options.onLeftOpenChange(preset.workspace.leftOpen)
      options.onRightOpenChange(preset.workspace.rightOpen)
      options.onSidebarTabChange(preset.workspace.sidebarTab)
    },
  })
  return { presets, layoutPreferences: layout.controls, panelWidths, onPanelWidthsChange }
}
